import { createContext, useContext, useState, useEffect, useRef } from "react";
import { db } from "../firebase.config.js";
import { collection, query, where, onSnapshot, orderBy, limit } from "firebase/firestore";
import { uploadFileEmitter } from "../components/UploadFileEmitter";

const NotificationContext = createContext();

/**
 * NotificationProvider
 * 
 * Counts documents in Firestore where readAt is false.
 * 
 * Props:
 * - orgId: Organization ID for filtering documents
 * - collectionName: Firestore collection name (default: "document_tasks")
 * 
 * Usage:
 * <NotificationProvider orgId="123" collectionName="document_tasks">
 */
export const NotificationProvider = ({ children, orgId, collectionName = "document_tasks" }) => {
   const [documentCount, setDocumentCount] = useState(0);
   const [documents, setDocuments] = useState([]);
   const [isUploading, setIsUploading] = useState(false);
   const unsubscribeRef = useRef(null);
   const unsubscribeDropdownRef = useRef(null);

   // Listen to uploadFileEmitter for upload status
   // useEffect(() => {
   //    const handleUploadStart = () => {
   //       setIsUploading(true);
   //    };

   //    const handleUploadEnd = () => {
   //       setIsUploading(false);
   //    };

   //    uploadFileEmitter.on("uploadStart", handleUploadStart);
   //    uploadFileEmitter.on("uploadEnd", handleUploadEnd);
   //    uploadFileEmitter.on("uploadComplete", handleUploadEnd);
   //    uploadFileEmitter.on("uploadError", handleUploadEnd);

   //    return () => {
   //       uploadFileEmitter.off("uploadStart", handleUploadStart);
   //       uploadFileEmitter.off("uploadEnd", handleUploadEnd);
   //       uploadFileEmitter.off("uploadComplete", handleUploadEnd);
   //       uploadFileEmitter.off("uploadError", handleUploadEnd);
   //    };
   // }, []);

   // Count documents where readAt is false (filtered by orgId)
   // Note: Firestore can't query for specific boolean values directly in some cases,
   // so we query all documents and filter client-side
   useEffect(() => {
      console.log("🔔 NotificationContext: useEffect triggered", { orgId, collectionName, orgIdType: typeof orgId });
      
      // Only run query if orgId is provided and is a valid string/number
      // Check for null, undefined, empty string, etc.
      if (!orgId || orgId === null || orgId === undefined || orgId === "") {
         console.log("⚠️ NotificationContext: No orgId, skipping query");
         setDocumentCount(0);
         setDocuments([]);
         // Clean up previous listener if it exists
         if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
         }
         return;
      }

      
      
   }, [orgId, collectionName]);

   /**
    * Fetch all documents without readAt (for dropdown)
    * This is called when the dropdown opens
    */
   const fetchAllDocuments = (callback) => {
      // Validate orgId before querying
      if (!orgId || orgId === null || orgId === undefined || orgId === "") {
         callback([]);
         return;
      }

      try {
         const q = query(
            collection(db, collectionName),
            where("org_id", "==", orgId),
            orderBy("doc_upload_date", "desc"), // Adjust field name as needed
            limit(20)
         );

         // Clean up previous dropdown listener if it exists
         if (unsubscribeDropdownRef.current) {
            unsubscribeDropdownRef.current();
         }

         unsubscribeDropdownRef.current = onSnapshot(q, (snapshot) => {
            // Filter documents where readAt is false
            const unreadDocs = snapshot.docs.filter((doc) => {
               const data = doc.data();
               return data.readAt === false;
            });
            
            const results = unreadDocs.map((doc) => ({ id: doc.id, ...doc.data() }));
            callback(results);
         }, (error) => {
            console.error("❌ Error fetching dropdown documents:", error);
            callback([]);
         });
      } catch (error) {
         console.error("❌ Error setting up dropdown query:", error);
         callback([]);
      }
   };

   /**
    * Cleanup dropdown listener
    */
   const cleanupDropdownListener = () => {
      if (unsubscribeDropdownRef.current) {
         unsubscribeDropdownRef.current();
         unsubscribeDropdownRef.current = null;
      }
   };

   const value = {
      documentCount,
      documents,
      isUploading,
      hasDocuments: documentCount > 0,
      fetchAllDocuments,
      cleanupDropdownListener,
   };

   return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => {
   const context = useContext(NotificationContext);
   if (!context) {
      throw new Error("useNotifications must be used within NotificationProvider");
   }
   return context;
};
